import type {
  FormPreviewMode,
  FormPreviewSnapshot,
  FormPreviewSource,
  FormPreviewStep,
  FormPreviewWarning,
} from '@/contracts/form-preview'
import type { QuestionOutlineItem } from '@/contracts/question-groups'
import type { QuestionGroupUiContract } from '@/contracts/questionnaire'
import { createSyntheticMeta } from '@/contracts/questionnaire'
import type { ContractSourceMeta } from '@/contracts/questionnaire'
import { isWelcomeScreenEnabled } from '@/lib/form-welcome-screen'
import { isLayoutQuestionType } from '@/types/form'
import type { Form, LogicJump, PublicForm, Question } from '@/types/form'

export type NormalizeFormPreviewSnapshotInput = {
  id?: string
  source: FormPreviewSource
  mode?: FormPreviewMode
  form: Form | PublicForm
  questions: Question[]
  groups?: QuestionGroupUiContract[]
  outline?: QuestionOutlineItem[]
  logicJumps?: LogicJump[]
  hiddenFields?: Record<string, string>
  sourceMeta?: ContractSourceMeta
  includeWelcomeCompletion?: boolean
}

type PreviewUnit =
  | { kind: 'question'; orderIndex: number; questionId: string }
  | { kind: 'group'; orderIndex: number; groupId: string }

export function normalizeFormPreviewSnapshot(
  input: NormalizeFormPreviewSnapshotInput,
): FormPreviewSnapshot {
  const groups = [...(input.groups ?? [])].sort(compareGroups)
  const warnings: FormPreviewWarning[] = []
  const questions = collectPreviewQuestions(input.questions, groups)
  const questionById = new Map(questions.map((question) => [question.id, question]))
  const groupById = new Map(groups.map((group) => [group.id, group]))
  const hiddenFields = input.hiddenFields ?? getPublicFormHiddenFields(input.form)
  const logicJumps = input.logicJumps ?? getPublicFormLogicJumps(input.form)
  const outline = input.outline ?? []
  const includeWelcomeCompletion = input.includeWelcomeCompletion ?? true
  const includeWelcome = includeWelcomeCompletion && isWelcomeScreenEnabled(input.form)
  const steps: FormPreviewStep[] = includeWelcome
    ? [{ kind: 'welcome', id: 'welcome', label: input.form.welcomeScreen.title || '開始頁' }]
    : []
  const seenQuestionIds = new Set<string>()

  const addQuestionStep = (questionId: string, groupId?: string) => {
    const question = questionById.get(questionId)
    if (!question) {
      warnings.push({
        code: 'missing_question',
        message: `預覽找不到題目 ${questionId}。`,
        questionId,
        groupId,
      })
      return
    }
    if (seenQuestionIds.has(question.id)) return
    seenQuestionIds.add(question.id)
    steps.push(toQuestionStep(question, groupId ?? question.groupId))
  }

  const addGroupStep = (groupId: string) => {
    const group = groupById.get(groupId)
    if (!group) {
      warnings.push({
        code: 'missing_group_target',
        message: `預覽找不到題組 ${groupId}。`,
        groupId,
      })
      return
    }

    const questionIds = resolveGroupQuestionIds(group, questions)
    steps.push({
      kind: 'group',
      id: `group:${group.id}`,
      groupId: group.id,
      label: group.title,
      description: group.description,
      mode: group.mode,
      lockedReason: group.lockedReason,
      questionIds,
    })

    if (questionIds.length === 0) {
      warnings.push({
        code: 'empty_group',
        message: `題組「${group.title}」沒有可預覽的題目。`,
        groupId: group.id,
      })
      return
    }

    for (const questionId of questionIds) addQuestionStep(questionId, group.id)
  }

  if (outline.length > 0) {
    for (const item of outline) {
      if (item.kind === 'question') {
        addQuestionStep(item.questionId, item.groupInstanceId ?? undefined)
      } else {
        addGroupStep(item.groupInstanceId)
      }
    }
  } else {
    for (const unit of buildFallbackPreviewUnits(questions, groups)) {
      if (unit.kind === 'question') addQuestionStep(unit.questionId)
      if (unit.kind === 'group') addGroupStep(unit.groupId)
    }
  }

  if (includeWelcomeCompletion) {
    steps.push({
      kind: 'completion',
      id: 'completion',
      label: input.form.thankYouScreen.title || '完成頁',
    })
  }

  return {
    id: input.id ?? `${input.form.id}:${input.source}:${input.mode ?? 'full_flow'}`,
    source: input.source,
    mode: input.mode ?? 'full_flow',
    form: {
      ...input.form,
      questions,
      logicJumps,
      hiddenFields,
    },
    questions,
    groups,
    outline,
    logicJumps,
    hiddenFields,
    steps,
    warnings,
    sourceMeta: input.sourceMeta ?? createSyntheticMeta(),
  }
}

function collectPreviewQuestions(questions: Question[], groups: QuestionGroupUiContract[]) {
  const byId = new Map<string, Question>()
  for (const question of questions) byId.set(question.id, question)
  for (const group of groups) {
    for (const question of group.questions ?? []) byId.set(question.id, question)
  }
  return [...byId.values()].sort(compareQuestions)
}

function buildFallbackPreviewUnits(
  questions: Question[],
  groups: QuestionGroupUiContract[],
): PreviewUnit[] {
  const groupedQuestionIds = new Set(
    groups.flatMap((group) => resolveGroupQuestionIds(group, questions)),
  )
  const ungroupedQuestionUnits: PreviewUnit[] = questions
    .filter((question) => !question.groupId || !groupedQuestionIds.has(question.id))
    .map((question) => ({
      kind: 'question' as const,
      orderIndex: question.orderIndex,
      questionId: question.id,
    }))
  const groupUnits: PreviewUnit[] = groups.map((group) => ({
    kind: 'group' as const,
    orderIndex: group.orderIndex,
    groupId: group.id,
  }))

  return [...ungroupedQuestionUnits, ...groupUnits].sort((left, right) => {
    if (left.orderIndex !== right.orderIndex) return left.orderIndex - right.orderIndex
    if (left.kind === right.kind) return 0
    return left.kind === 'group' ? -1 : 1
  })
}

function resolveGroupQuestionIds(group: QuestionGroupUiContract, questions: Question[]) {
  if (group.questionIds.length > 0) return group.questionIds
  if (group.questions && group.questions.length > 0)
    return group.questions.sort(compareQuestions).map((question) => question.id)
  return questions
    .filter((question) => question.groupId === group.id)
    .sort(compareQuestions)
    .map((question) => question.id)
}

function toQuestionStep(question: Question, groupId?: string): FormPreviewStep {
  return {
    kind: 'question',
    id: `question:${question.id}`,
    questionId: question.id,
    groupId,
    label: question.title || '未命名題目',
    answerable: !isLayoutQuestionType(question.type),
  }
}

function getPublicFormLogicJumps(form: Form | PublicForm) {
  return 'logicJumps' in form ? form.logicJumps : []
}

function getPublicFormHiddenFields(form: Form | PublicForm) {
  return 'hiddenFields' in form ? form.hiddenFields : {}
}

function compareGroups(left: QuestionGroupUiContract, right: QuestionGroupUiContract) {
  return left.orderIndex - right.orderIndex || left.title.localeCompare(right.title, 'zh-Hant')
}

function compareQuestions(left: Question, right: Question) {
  return left.orderIndex - right.orderIndex || left.title.localeCompare(right.title, 'zh-Hant')
}
